import { Reveal, Eyebrow } from "@/components/Reveal";
import { COMPANY } from "@/data/content";

const SECTIONS = [
  {
    title: "1. Who we are",
    body: `Invesmo Pty Ltd (ABN ${COMPANY.abn}) ("Invesmo", "we", "us") is a mortgage and finance broking business based in Camberwell VIC 3124. Invesmo Pty Ltd, Credit Representative Number ${COMPANY.crn}, is an authorised credit representative of ${COMPANY.aggregator}, holder of Australian Credit Licence ${COMPANY.acl}. This policy explains how we collect, hold, use and disclose personal information in accordance with the Privacy Act 1988 (Cth) and the Australian Privacy Principles (APPs).`,
  },
  {
    title: "2. What we collect",
    body: `We collect personal information you provide through this website and in the course of providing credit assistance — including your name, contact details, and information about your financial position, objectives and requirements. When you book a meeting or send an enquiry through this site, we collect your name, email address, phone number, the service you are interested in, your preferred meeting time, and anything you choose to tell us in free-text fields.`,
  },
  {
    title: "3. Why we collect it",
    body: `We use your personal information to: arrange and conduct consultations; assess which lenders and products may suit your needs; prepare and submit credit applications on your behalf (with your consent); manage our ongoing relationship with you, including reviews; and meet our legal and regulatory obligations as a credit representative under the National Consumer Credit Protection Act 2009 (Cth).`,
  },
  {
    title: "4. Who we disclose it to",
    body: `With your consent, we may disclose your information to: lenders and credit providers on the LMG panel; our aggregator, ${COMPANY.aggregator}, for compliance, licensing and administrative purposes; credit reporting bodies where relevant to an application; your professional advisers (such as your accountant) where you ask us to; and government or regulatory bodies where required by law. We do not sell personal information, and we do not disclose it to third parties for their marketing.`,
  },
  {
    title: "5. How we hold and secure it",
    body: `Personal information is stored in secure electronic systems with access restricted to authorised personnel. We take reasonable steps to protect information from misuse, interference, loss, and unauthorised access, modification or disclosure. When information is no longer required, we take reasonable steps to destroy or de-identify it, subject to record-keeping obligations under credit law.`,
  },
  {
    title: "6. Access and correction",
    body: `You may request access to the personal information we hold about you, or ask us to correct it, at any time by contacting us at ${COMPANY.email} or ${COMPANY.phoneDisplay}. We will respond within a reasonable period and, in most cases, provide access free of charge.`,
  },
  {
    title: "7. Complaints",
    body: `If you believe we have breached the Privacy Act or handled your information improperly, contact us first at ${COMPANY.email} and we will investigate and respond. If you are not satisfied with our response, you may complain to the Australian Financial Complaints Authority (AFCA) — of which Invesmo Pty Ltd is a member (membership number ${COMPANY.afcaMember}) — at afca.org.au or 1800 931 678, or to the Office of the Australian Information Commissioner (OAIC) at oaic.gov.au.`,
  },
  {
    title: "8. Website data",
    body: `This website may use cookies and analytics tools to understand how visitors use the site and to improve it. This data is collected in aggregate and is not used to personally identify you. You can disable cookies in your browser settings, though some site features may not function as intended.`,
  },
  {
    title: "9. Changes to this policy",
    body: `We may update this policy from time to time. The current version will always be published at ${COMPANY.domain}/privacy. This policy was last updated in July 2026.`,
  },
];

export default function Privacy() {
  return (
    <div data-testid="privacy-page" className="pt-36 sm:pt-44 pb-24">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <Reveal>
          <Eyebrow>Legal</Eyebrow>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-ink leading-[1.05]">Privacy Policy</h1>
          <p className="mt-5 text-ink/55 leading-relaxed max-w-2xl">
            Your privacy matters. This policy sets out how Invesmo collects, uses, holds and protects your personal
            information under Australian law.
          </p>
        </Reveal>

        <div className="mt-14 space-y-10">
          {SECTIONS.map((s, i) => (
            <Reveal key={s.title} delay={Math.min(i * 0.04, 0.2)}>
              <section data-testid={`privacy-section-${i}`} className="border-b border-ink/8 pb-10">
                <h2 className="font-display text-xl sm:text-2xl font-bold text-ink mb-4">{s.title}</h2>
                <p className="text-ink/60 leading-relaxed text-[15px]">{s.body}</p>
              </section>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="mt-14 rounded-3xl bg-obsidian text-white/65 p-8 sm:p-10 grain relative overflow-hidden" data-testid="privacy-disclosures">
            <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-gold/10 blur-3xl pointer-events-none" />
            <h2 className="font-display text-xl font-bold text-white mb-5">Important disclosures</h2>
            <div className="space-y-4 text-xs leading-relaxed">
              <p>
                <span className="text-gold font-semibold">General advice warning.</span> The information on this website
                is general in nature and does not constitute financial, legal, taxation or credit advice. It has been
                prepared without taking into account your objectives, financial situation or needs. Before acting on any
                information on this site, you should consider its appropriateness to your circumstances and seek
                professional advice. All credit applications are subject to lender credit assessment, eligibility
                criteria, terms, conditions, fees and charges.
              </p>
              <p>
                <span className="text-gold font-semibold">Credit representative details.</span> Invesmo Pty Ltd (ABN{" "}
                {COMPANY.abn}), Credit Representative Number {COMPANY.crn}, is an authorised credit representative of{" "}
                {COMPANY.aggregator}, Australian Credit Licence {COMPANY.acl}. Invesmo Pty Ltd is a member of the
                Australian Financial Complaints Authority, membership number {COMPANY.afcaMember}. A copy of our Credit
                Guide — which sets out our obligations, remuneration and dispute resolution arrangements — is available
                on request by emailing {COMPANY.email}.
              </p>
              <p>
                <span className="text-gold font-semibold">Calculator results.</span> Figures produced by calculators on
                this website are estimates only, provided for illustrative purposes, and do not constitute a quote, an
                offer of credit, or a representation of borrowing capacity.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
